<template>
  <div class="d-flex justify-space-between w-100 flex-column">
    <div>
      <div class="ion-padding-bottom d-flex w-100 align-center">
        <slot name="prepend" />

        <p class="font-md bold-medium blue-darker pl-xs">{{ t('booking.billing.title') }}</p>
      </div>
      <p class="subtitle grey">
        {{ t('booking.billing.description') }}
      </p>

      <div class="block bg-white w-100 mt-sm mb-xs">
        <ion-radio-group
          v-model="saveMode"
          value="custom-checked"
          class="d-flex justify-space-between radio-documents"
          @ion-change="onChangeMode"
        >
          <ion-radio value="people">{{ t('booking.billing.billingSubject.people') }}</ion-radio>
          <ion-radio value="entity">{{ t('booking.billing.billingSubject.entity') }}</ion-radio>
        </ion-radio-group>
      </div>

      <div v-if="saveMode === 'people'" class="d-flex ion-padding-bottom align-center">
        <ion-toggle v-model="useProfile" :checked="useProfile" @click="onChange"></ion-toggle>
        <p class="font-sm padding-list-item">{{ t('booking.billing.complete') }}</p>
      </div>
    </div>

    <div class="costs-list">
      <div class="py-sm">
        <ion-input
          v-model="profileBillingDataForm.name"
          type="text"
          mode="md"
          shape="round"
          class="custom-field"
          :label="
            saveMode === 'people' ? t('booking.billing.name') : t('booking.billing.commercial')
          "
          label-placement="stacked"
          fill="outline"
          :placeholder="
            saveMode === 'people' ? t('booking.billing.name') : t('booking.billing.commercial')
          "
        >
        </ion-input>
      </div>
      <div class="py-sm">
        <ion-select
          fill="outline"
          color="primary"
          v-model="profileBillingDataForm.documentType"
          :label="t('booking.billing.idType')"
          label-placement="stacked"
        >
          <ion-select-option
            v-for="document in documentOptions"
            :value="document.id"
            :key="document.id"
            >{{ document.label }}</ion-select-option
          >
        </ion-select>
      </div>
      <div class="py-sm">
        <ion-input
          v-model="profileBillingDataForm.documentId"
          mode="md"
          shape="round"
          class="custom-field"
          :label="t('booking.billing.documentId')"
          label-placement="stacked"
          fill="outline"
          :placeholder="t('booking.billing.documentId')"
        >
        </ion-input>
      </div>

      <div class="py-sm">
        <ion-col class="bg-white ion-no-padding" size="12">
          <ion-searchbar
            class="custom"
            :debounce="400"
            @input="handleInput($event)"
            v-model="fullAdress as string"
            :placeholder="t('announcement.location')"
          ></ion-searchbar>

          <div class="relative-container">
            <ion-card
              v-if="results.length > 0"
              class="bg-white ion-padding results-card results no-margin-top"
            >
              <p
                v-for="(result, idx) in results"
                @click="setLocation(result)"
                :key="`location-${idx}`"
              >
                {{ result.fullAddress }}
              </p>
            </ion-card>
          </div>
        </ion-col>
      </div>

      <div class="py-sm">
        <ion-input
          v-model="profileBillingDataForm.email"
          type="email"
          mode="md"
          shape="round"
          class="custom-field"
          :label="t('booking.billing.email')"
          label-placement="stacked"
          fill="outline"
          :placeholder="t('booking.billing.email')"
        >
          <ion-icon slot="start" :icon="mailOutline" aria-hidden="true"></ion-icon>
        </ion-input>
      </div>
    </div>

    <div class="d-flex justify-space-between">
      <div></div>
      <!--  <ion-button
        fill="clear"
        expand="block"
        size="default"
        color="primary"
        class="text-lg text-weight-bold"
        @click="$emit('back')"
      >
        {{ t('global.back') }}
        <ion-icon slot="start" :icon="chevronBackOutline" aria-hidden="true"></ion-icon>
      </ion-button> -->

      <ion-button
        :disabled="!getDisabled || isLoading"
        fill="solid"
        expand="block"
        size="default"
        color="primary"
        class="text-lg text-weight-bold"
        @click="editTenantData"
      >
        <ion-icon slot="end" :icon="chevronForwardOutline" aria-hidden="true"></ion-icon>
        {{ t('global.continue') }}
      </ion-button>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { useI18n } from 'vue-i18n';
  import { chevronForwardOutline, mailOutline } from 'ionicons/icons';
  import {
    IonButton,
    IonIcon,
    IonCol,
    IonSearchbar,
    IonSelect,
    IonSelectOption,
    IonCard,
    IonToggle,
    IonInput,
    IonRadioGroup,
    IonRadio,
  } from '@ionic/vue';
  import { storeToRefs } from 'pinia';
  import { useBookingStore } from '@/stores/booking-store';
  import { computed, onMounted, ref, watch } from 'vue';
  import { useMapsStore } from '@/stores/maps-store';
  import { useToast } from '@/composables/useToast';
  import { PlaceLocation } from '@/interfaces/elastic/elastic.interface';
  import { validEmail, alphanumericPattern } from '@/utils/forms/schemas';
  import { useDocuments } from '@/utils/constants/general-constants';

  const emit = defineEmits(['continue', 'back']);
  const documentOptions = useDocuments();

  const { t } = useI18n();
  const { storedDataForm, profileBillingDataForm, isLoading } = storeToRefs(useBookingStore());
  const { editTenantBillingInfo } = useBookingStore();
  const { searchDirection } = useMapsStore();
  const { results } = storeToRefs(useMapsStore());

  const { showToast } = useToast();
  const saveMode = ref('people');
  const useProfile = ref(true);
  const fullAdress = ref(profileBillingDataForm.value.Location.fullAddress);

  const getDisabled = computed(() => {
    const isValidEmail = profileBillingDataForm.value.email.match(validEmail);
    const isValidDocumentId = profileBillingDataForm.value.documentId.match(alphanumericPattern);
    const value =
      profileBillingDataForm.value.name &&
      profileBillingDataForm.value.documentType &&
      profileBillingDataForm.value.documentId &&
      profileBillingDataForm.value.email &&
      profileBillingDataForm.value.Location.address &&
      isValidEmail &&
      isValidDocumentId;

    return value;
  });

  const editTenantData = async () => {
    try {
      isLoading.value = true;

      const response = await editTenantBillingInfo();
      if (response.success) {
        await showToast(response.msg as string);
        emit('continue');
      } else {
        throw new Error(response.error);
      }
    } catch (error) {
      await showToast(error instanceof Error ? error.message : 'Unknown error', 'error');
    } finally {
      isLoading.value = false;
    }
  };

  const handleInput = (event: any) => {
    const query = event.target.value.toLowerCase();

    if (query.length > 2) {
      searchDirection(query);
    } else if (query === '') {
      results.value = [];
      profileBillingDataForm.value.Location = {
        address: null,
        fullAddress: null,
        lat: null,
        lon: null,
        country: null,
        countryCode: null,
        prefixPhone: null,
        state: null,
        city: null,
        postalCode: null,
      };
      fullAdress.value = null;
    }
  };

  const setLocation = (location: PlaceLocation) => {
    profileBillingDataForm.value.Location = location;
    fullAdress.value = location.fullAddress;
    results.value = [];
  };

  const onChange = () => {
    if (!useProfile.value) {
      setStoredData();
    } else {
      clearData();
    }
  };

  const clearData = () => {
    fullAdress.value = null;
    profileBillingDataForm.value = {
      email: '',
      name: '',
      documentId: '',
      documentType: '',
      Location: {
        address: null,
        fullAddress: null,
        lat: null,
        lon: null,
        country: null,
        countryCode: null,
        prefixPhone: null,
        state: null,
        city: null,
        postalCode: null,
      },
    };
  };

  const onChangeMode = () => {
    if (saveMode.value !== 'people') {
      clearData();
    } else if (useProfile.value) {
      setStoredData();
    }
  };

  const setStoredData = () => {
    profileBillingDataForm.value = { ...storedDataForm.value };
    fullAdress.value = profileBillingDataForm.value.Location
      ? profileBillingDataForm.value.Location.fullAddress
      : '';
  };

  watch(storedDataForm, () => {
    setStoredData();
  });

  onMounted(async () => {
    setStoredData();
  });
</script>

<style lang="scss" scoped>
  ion-modal {
    --height: auto;
  }

  ion-item {
    --ion-background-color: white !important;
  }

  .justify-space-between {
    justify-content: space-between;
  }

  :host(.select-label-placement-floating) .select-wrapper {
    margin-top: -5px !important;
  }

  .results {
    position: fixed;
    z-index: 99;
    width: 80%;
  }

  .relative-container {
    position: absolute;
    z-index: 99;
    width: 90%;
    top: 15px;
  }
  .sc-ion-searchbar-md {
    display: none !important;
  }
</style>
